import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Snackbar } from '@mui/material';

import SimpleModal from '../../components/SimpleModal';
import {
  WILL_APPLY_TO_ALL_ROUTES,
  DISASTER_RECOVERY,
  SWITCH_TO_DISASTER_RECOVERY,
} from '../../constants/strings';
import { validateFormValues } from './validators';
import DisasterRecoveryForm from './components/DisasterRecoveryForm';
import ModalControlled from '../../components/ModalControlled';
import { WarningIcon } from '../../components/icons';
import ConfirmSnackbar from '../../components/modals/ConfirmSnackbar';
import { wrapPromise } from '../../utils/promise';
import { getErrorMessage } from '../../utils/errors';
import { setCutOffTime, setDisasterRecoveryMode } from '../../services/routes';
import { toMinutes } from '../../utils/date';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import useLoader from '../../hooks/useLoader';
import { RECOVERY_ACCESS_PROCESS } from '../../enums/loaderProcess';
import { formatMessage } from '../../utils/string';
import { isDisasterRecoveryMode } from '../../store/selectors/app';

const RecoveryAccess = ({ open, onClose }) => {
  const saveChangesModalRef = useRef();
  const [message, setErrorMessage] = useState('');
  const { date, depot } = useLocationQueryParams();
  const [showLoader, hideLoader] = useLoader();
  const isDRmode = useSelector(isDisasterRecoveryMode);

  const initialValues = useMemo(
    () => ({
      waveCutOff: '00:00',
      departTime: '00:00',
    }),
    []
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: (data) => {
      const errors = validateFormValues(data);
      return { values: data, errors };
    },
  });

  const closeSnackbar = useCallback(() => {
    setErrorMessage('');
  }, [setErrorMessage]);

  const handleConfirm = useCallback(
    async (values) => {
      try {
        const [, error] = await wrapPromise(
          saveChangesModalRef.current?.showModal({
            message: WILL_APPLY_TO_ALL_ROUTES,
          })
        );
        if (error) {
          return;
        }
        showLoader(RECOVERY_ACCESS_PROCESS);
        const success = await setCutOffTime(depot, date, {
          waveCutOff: toMinutes(values.waveCutOff),
          departTime: toMinutes(values.departTime),
        });
        success && onClose();
      } catch (e) {
        setErrorMessage(getErrorMessage(e));
      } finally {
        hideLoader(RECOVERY_ACCESS_PROCESS);
      }
    },
    [depot, date, showLoader, hideLoader, onClose]
  );

  const handleSwitchToDisaster = useCallback(async () => {
    try {
      const [, error] = await wrapPromise(
        saveChangesModalRef.current?.showModal({
          message: formatMessage(SWITCH_TO_DISASTER_RECOVERY, depot),
        })
      );
      if (error) {
        return;
      }
      showLoader(RECOVERY_ACCESS_PROCESS);
      const success = await setDisasterRecoveryMode(depot);
      success && onClose();
    } catch (e) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      hideLoader(RECOVERY_ACCESS_PROCESS);
    }
  }, [depot, showLoader, hideLoader, onClose]);

  return (
    <SimpleModal
      onClose={onClose}
      open={open}
      title={DISASTER_RECOVERY.toUpperCase()}
    >
      <form style={{ width: '100%' }}>
        <DisasterRecoveryForm
          control={control}
          values={watch()}
          handleConfirm={handleSubmit(handleConfirm)}
          handleSwitchToDisasterRecoveryMode={handleSwitchToDisaster}
          dirty={isDirty}
          isValid={isValid}
          isDRmode={isDRmode}
          initialValues={initialValues}
          errors={errors}
        />
      </form>
      <ModalControlled
        Component={ConfirmSnackbar}
        ref={saveChangesModalRef}
        messageIcon={<WarningIcon />}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!message}
        autoHideDuration={2000}
        message={message}
        onClose={closeSnackbar}
      />
    </SimpleModal>
  );
};

export default memo(RecoveryAccess);
